import type { ReactNode } from "react";
import type { StylesConfig } from "react-select";
import {
  components as ReactSelectComponents,
  type ControlProps,
  type FormatOptionLabelMeta,
  type GroupBase,
  type SelectComponentsConfig
} from "react-select";
import { Select } from "shared/components";
import { cx } from "shared/utils/classnames";
import type { BaseOption, Option } from "shared/components";
import {
  FOOTER_PILL_STYLES,
  withWarningBorder
} from "./participantFieldStyles";
import { formatSenderOptionLabel } from "../utils/senderOption";

type FieldLabel = "From" | "To";

const FieldLabelText = ({
  label,
  className
}: {
  label: FieldLabel;
  className?: string;
}) => (
  <span
    className={cx("shrink-0 text-xs font-medium text-content-400", className)}
  >
    {label}:
  </span>
);

const withFieldLabel = (label: FieldLabel) => {
  const LabeledControl = ({
    children,
    ...props
  }: ControlProps<unknown, boolean, GroupBase<unknown>>) => (
    <ReactSelectComponents.Control {...props}>
      <FieldLabelText label={label} className="pl-2" />
      {children}
    </ReactSelectComponents.Control>
  );
  return LabeledControl;
};

const FIELD_COMPONENTS: Record<
  FieldLabel,
  SelectComponentsConfig<unknown, boolean, GroupBase<unknown>>
> = {
  From: { Control: withFieldLabel("From") },
  To: { Control: withFieldLabel("To") }
};

const ROW_GAP_CLASS = "gap-2";
const ROW_GAP_PX = 8;

const fieldStyles = (menuAlign: "start" | "end"): StylesConfig => ({
  ...FOOTER_PILL_STYLES,
  valueContainer: (base) => ({
    ...base,
    minWidth: 0,
    flexWrap: "nowrap",
    padding: "0 0 0 4px"
  }),
  menu: (base) => ({
    ...base,
    width: `calc(200% + ${ROW_GAP_PX}px)`,
    ...(menuAlign === "end" ? { left: "auto", right: 0 } : { left: 0 })
  })
});

const FIELD_STYLES: Record<FieldLabel, StylesConfig> = {
  From: fieldStyles("start"),
  To: fieldStyles("end")
};

const FIELD_WARNING_STYLES: Record<FieldLabel, StylesConfig> = {
  From: withWarningBorder(FIELD_STYLES.From),
  To: withWarningBorder(FIELD_STYLES.To)
};

export const CompactRoutingRow = ({ children }: { children: ReactNode }) => (
  <div className={`flex w-full min-w-0 flex-row flex-nowrap ${ROW_GAP_CLASS}`}>
    {children}
  </div>
);

export const CompactRoutingField = ({
  id,
  label,
  value,
  valueText,
  options,
  onChange,
  menuPlacement,
  isDisabled,
  placeholder,
  warning
}: {
  id: string;
  label: FieldLabel;
  value: string;
  valueText: string;
  options: BaseOption[];
  onChange: (value: string) => void;
  menuPlacement?: "top" | "bottom" | "auto";
  isDisabled?: boolean;
  placeholder?: string;
  warning?: boolean;
}) => {
  const formatOptionLabel = (
    option: Option,
    meta: FormatOptionLabelMeta<Option>
  ): ReactNode =>
    meta.context === "value" ? (
      <span title={valueText}>{valueText}</span>
    ) : (
      formatSenderOptionLabel(option, meta)
    );

  return (
    <div className="min-w-0 flex-1">
      <Select
        id={id}
        hideOptional
        searchable={false}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        options={options}
        className="font-medium"
        styles={warning ? FIELD_WARNING_STYLES[label] : FIELD_STYLES[label]}
        components={FIELD_COMPONENTS[label]}
        formatOptionLabel={formatOptionLabel}
        legacyErrorMargins={false}
        isDisabled={isDisabled}
        menuPlacement={menuPlacement}
        placeholder={placeholder}
      />
    </div>
  );
};

export const CompactRoutingEmpty = ({
  label,
  message
}: {
  label: FieldLabel;
  message: string;
}) => (
  <div className="flex h-10 min-w-0 flex-1 items-center gap-1 rounded-lg border border-alpha-100 bg-white px-2 shadow-xs">
    <FieldLabelText label={label} />
    <span
      title={message}
      className="truncate text-xs font-medium text-content-300"
    >
      {message}
    </span>
  </div>
);
